import { MetodoAplicacion } from "./response/AplicacionResponse";

/** Mililitros en un litro. El factor aparece en las tres conversiones de este
 *  archivo (mezcla del drench, litros de producto) y es el típico `1000` que
 *  después nadie sabe de dónde salió. */
const ML_POR_LITRO = 1000;

/** Con qué se está midiendo la aplicación: lo que no cambia de lote a lote.
 *
 *  Sale del formulario mientras se edita y de la aplicación guardada al leerla
 *  —ahí los litros son los **congelados** (`volumenRecipienteLitros`), no los
 *  que el recipiente tenga hoy. */
export type ParametrosMedicion = {
  metodo: MetodoAplicacion;
  /** Litros que le caben al recipiente elegido. */
  litrosRecipiente: number;
  /** Mililitros de mezcla por planta. Solo se usa en drench. */
  mezclaPorPlantaMl: number;
};

/** Un lote de la aplicación con lo necesario para calcularlo. */
export type LoteMedido = {
  loteId: string;
  loteNombre: string;
  /** Área del lote, para la descarga por hectárea. */
  areaHectareas: number;
  /** Recipientes aplicados (`null` en drench). */
  cantidad: number | null;
  /** Plantas tratadas (`null` fuera de drench). */
  plantas: number | null;
};

/**
 * Litros de mezcla que se aplicaron en el lote.
 *
 * Es **la** conversión del módulo: lo que el usuario anota en campo —recipientes
 * llenos, o plantas en drench— pasa acá a litros, que es la unidad en la que se
 * mide todo lo demás (descarga por hectárea, volumen total, litros de producto).
 */
export function getMezclaLitros(
  lote: LoteMedido,
  parametros: ParametrosMedicion,
): number {
  if (parametros.metodo === "drench") {
    return ((lote.plantas ?? 0) * parametros.mezclaPorPlantaMl) / ML_POR_LITRO;
  }
  return (lote.cantidad ?? 0) * parametros.litrosRecipiente;
}

/**
 * Recipientes equivalentes del lote.
 *
 * Fuera de drench es lo que el usuario escribió. En drench se deduce de la
 * mezcla, porque ahí la cantidad se anota en plantas: "2.000 plantas a 250 ml"
 * son 500 L, o sea 2,5 canecas de 200. Hace falta porque **la dosis del producto
 * es por recipiente**, también en drench.
 */
export function getRecipientesEquivalentes(
  lote: LoteMedido,
  parametros: ParametrosMedicion,
): number {
  if (parametros.metodo !== "drench") return lote.cantidad ?? 0;
  if (parametros.litrosRecipiente <= 0) return 0;
  return getMezclaLitros(lote, parametros) / parametros.litrosRecipiente;
}

/**
 * Litros de producto comercial que entraron al lote, con la dosis en ml por
 * recipiente. Es lo que alimenta el gráfico de producto por lote.
 */
export function getLitrosProducto(
  lote: LoteMedido,
  parametros: ParametrosMedicion,
  dosisMlPorRecipiente: number,
): number {
  return (
    (getRecipientesEquivalentes(lote, parametros) * dosisMlPorRecipiente) /
    ML_POR_LITRO
  );
}

/**
 * Descarga del lote en litros por hectárea: el número con el que se compara un
 * lote contra otro y contra la etiqueta del producto.
 *
 * Un lote sin área devuelve 0 en vez de infinito. No deberían existir, pero un
 * `Infinity` se propaga al promedio y deja toda la pantalla en "∞".
 */
export function getDescargaLitrosHa(
  lote: LoteMedido,
  parametros: ParametrosMedicion,
): number {
  if (lote.areaHectareas <= 0) return 0;
  return getMezclaLitros(lote, parametros) / lote.areaHectareas;
}

/** Totales de la aplicación: los cuatro KPI de la cabecera. */
export type ResumenAplicacion = {
  /** Recipientes equivalentes sumados. */
  recipientes: number;
  /** Plantas tratadas sumadas (0 fuera de drench). */
  plantas: number;
  /** Mezcla total, en litros. */
  mezclaLitros: number;
  /** Área tratada, en hectáreas. */
  areaHectareas: number;
  /** Descarga promedio, en litros por hectárea. */
  descargaLitrosHa: number;
};

/**
 * Totales de la aplicación.
 *
 * La descarga promedio es **mezcla total / área total**, no el promedio de las
 * descargas de cada lote: esas dos cuentas no dan lo mismo cuando los lotes
 * tienen áreas distintas, y la que describe la aplicación es la primera.
 */
export function getResumen(
  lotes: readonly LoteMedido[],
  parametros: ParametrosMedicion,
): ResumenAplicacion {
  let recipientes = 0;
  let plantas = 0;
  let mezclaLitros = 0;
  let areaHectareas = 0;

  for (const lote of lotes) {
    recipientes += getRecipientesEquivalentes(lote, parametros);
    plantas += lote.plantas ?? 0;
    mezclaLitros += getMezclaLitros(lote, parametros);
    areaHectareas += lote.areaHectareas;
  }

  return {
    recipientes,
    plantas,
    mezclaLitros,
    areaHectareas,
    descargaLitrosHa: areaHectareas > 0 ? mezclaLitros / areaHectareas : 0,
  };
}
